import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import FingeringForm from './components/FingeringForm';
import DriveSaveButton from './components/DriveSaveButton';
import DriveLinkCopyButton from './components/DriveLinkCopyButton';
import DriveFooterBar from './components/DriveFooterBar';
import AddToHomeModal from './components/AddToHomeModal';
import SubmittingOverlay from './components/SubmittingOverlay';
import CompletionBanner from '../../components/CompletionBanner';
import { AppFooter } from '../../components/AppFooter';
import { Copyright } from '../../components/Copyright';
import CompletionModal from './components/CompletionModal';
import ErrorModal from './components/ErrorModal';
import PdfDownloadSplitButton from './components/PdfDownloadSplitButton';
import ActiveJobsBadge from './components/ActiveJobsBadge';
import SectionDivider from './components/SectionDivider';
import { AutoSaveToggle } from './components/AutoSaveToggle';
import { NotifyToggle } from './components/NotifyToggle';
import { setHistoryLastSeen } from '../../utils/historyLastSeen';
import { useGoogleDriveSaver } from '../../hooks/useGoogleDriveSaver';
import { useAddToHomePrompt } from '../../hooks/useAddToHomePrompt';
import { useJobLifecycleResume } from '../../hooks/useJobLifecycleResume';
import { useBeforeUnloadGuard } from '../../hooks/useBeforeUnloadGuard';
import { useHealthStatus } from '../../contexts/HealthStatusContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { fingeringApi, ProcessingError } from '../../services/fingeringApi';
import type { JobProgress, RegenerateNotifyOptions } from '../../services/fingeringApi';
import { FingeringPart, FingeringMode, FingeringNavigateState, PendingAction, PdfColorMode, AutoSaveStatus, FingeringRestoreRequest } from '../../types/fingering';
import { retryFailedJob } from '../../utils/retryFailedJob';
import { getPdfColorMode, setPdfColorMode as savePdfColorMode } from '../../utils/pdfColorMode';
import { getAutoSaveEnabled, setAutoSaveEnabled as persistAutoSaveEnabled } from '../../utils/autoSaveSettings';
import { getNotifyEnabled, getNotifyEmail, validateNotifySettings, resolveNotifyEmail } from '../../utils/notifySettings';
import { useDriveAuth } from '../../contexts/DriveAuthContext';
import { ensureAppFolder } from '../../utils/driveApi';
import {
  saveFingeringResult,
  loadFingeringResult,
  savePendingOriginalPdf,
  clearPendingOriginalPdf,
  clearFingeringResult,
  purgeExpiredPendingOriginalPdfs,
  FingeringStorageResult,
} from '../../utils/fingeringStorage';
import {
  saveJobLifecycleEntry,
  clearJobLifecycleEntry,
  listEntriesNeedingReturnNotification,
  listDoneEntriesForAutoRestore,
  loadJobLifecycleEntry,
  markEntryNotified,
  type JobLifecycleEntry,
  savePendingSubmitMarker,
  clearPendingSubmitMarker,
  type JobType,
  type DriveSaveTarget,
} from '../../utils/jobLifecycleStorage';
import { restoreJobResultCore } from '../../utils/restoreJobResultCore';
import {
  buildBaseName,
  buildResultFileName,
  partNumFromPart,
  toOriginalTitle,
  resolveOriginalTitle,
} from '../../utils/fileNaming';
import {
  getSavedFolderTarget,
  type DriveTargetFolder,
} from '../../utils/driveTargetFolder';
// issue #154: Drive 保存済みフラグ (localStorage 永続)。 webViewLink / fileId は永続化しない。
import { isDriveSaved, markDriveSaved, clearDriveSaved } from '../../utils/driveSaveState';
// issue #177: 完成PDFの fileId 控え。 再生成時は中身が変わるため古い控えも捨てる。
import { clearDrivePdfFileId } from '../../utils/drivePdfLink';
import { features } from '../../config/features';
import { PART_LABELS } from '../../constants/fingering';
import { JOB_LIFECYCLE_TIMEOUT_MS } from '../../constants/timeouts';
import { useToast } from '../../components/ToastProvider';
import { useJobTracker } from '../../contexts/JobTrackerContext';
import { getFingeringErrorDetail } from '../../constants/fingeringErrorMessages';

// issue #76 Phase 5-β: errorKind と raw detail から `*_MESSAGE.replace('{detail}', err.message)`
// 相当の複数行文言を生成する。 detail なし (未知 errorKind) は raw detail 単独を返す (#54 fallback)。
function buildFingeringErrorMessage(errorKind: string, detail: string): string {
  const info = getFingeringErrorDetail(errorKind);
  if (!info) return detail;
  const lines: string[] = [info.title, ''];
  if (info.cause) {
    lines.push(info.cause, '');
  }
  lines.push('おすすめの対処:');
  for (const a of info.actions) {
    lines.push(`• ${a}`);
  }
  lines.push('', `詳細: ${detail}`);
  return lines.join('\n');
}

// ProcessingError + errorKind は共有定数経由で複数行文言を構築し、 それ以外は raw message か fallback を返す。
function resolveSubmitError(err: unknown, fallback: string): string {
  if (err instanceof ProcessingError && err.errorKind && getFingeringErrorDetail(err.errorKind)) {
    return buildFingeringErrorMessage(err.errorKind, err.message);
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Blob object URL をアンカークリックでダウンロードする。 url が null のとき no-op。 */
function downloadBlobUrl(url: string | null, fileName: string): void {
  if (!url) return;
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
}

/**
 * Drive 保存 (omfz 名) へ渡す part label。 part 未確定時は 'unknown' ではなく空文字を渡し、
 * omfz 名に infix を付けさせない (issue #152: unknown 混入防止)。
 */
function toPartLabel(part: FingeringPart | null | undefined): string {
  return part ? PART_LABELS[part] : '';
}

/**
 * fingering.json の meta を安全に取り出す (issue #152)。 part / title は命名ソースの解決材料。
 * meta.part は再生成時の part 復元 (state に無いケース) に、 meta.title は legacy 元タイトルの
 * fallback に使う。 壊れた JSON は空 meta として扱う。
 */
function readStoredMeta(fingeringJson?: string): { title?: string; part?: string } {
  if (!fingeringJson) return {};
  try {
    const parsed = JSON.parse(fingeringJson) as { meta?: { title?: string; part?: string } };
    return parsed.meta ?? {};
  } catch {
    return {};
  }
}

/** meta.part ('Guitar1' 等) を FingeringPart に narrow する。 未知値は null (part 不明扱い)。 */
function toFingeringPart(part: string | undefined): FingeringPart | null {
  return part && part in PART_LABELS ? (part as FingeringPart) : null;
}

/**
 * 結果 PDF 名を組む (issue #91: backend file_naming.py と同一規則)。
 * issue #152: 第 1 引数は「元タイトル」。 (元タイトル, part, mode) の純関数なので、 同じ入力なら
 * 何度呼んでも同じ名前になる (再生成での名前ぶれ・サフィックス重複が構造的に起きない)。
 */
function buildOutputFileName(originalTitle: string, part: FingeringPart, mode: FingeringMode): string {
  return buildResultFileName(buildBaseName(originalTitle), partNumFromPart(part), mode, 'pdf');
}

/**
 * 完了モーダル確定後の後始末 (issue #85 / #126)。 「結果を開く」「閉じる」どちらの確定でも共通に、
 * 通知済み化 + entry 削除 + 先行 PDF (pending-pdfs) 削除 + 履歴既読を行う。
 * issue #126: entry と先行 PDF のライフサイクルを一致させる (core での即削除を廃止し確定へ後ろ倒し)。
 */
function finalizeCompletedJob(jobId: string): void {
  markEntryNotified(jobId);
  clearJobLifecycleEntry(jobId);
  void clearPendingOriginalPdf(jobId);
  setHistoryLastSeen();
}

type RegenerateResult = {
  pdfBlob?: Blob;
  audioBlob?: Blob;
  audioFileName?: string;
  fingeringJson?: string;
  mxlBlob?: Blob;
};

async function dispatchRegenerate(
  action: PendingAction,
  fingeringJson: string,
  originalPdfBlob: Blob,
  mxlBlob: Blob | null,
  bpm: number | undefined,
  navState: FingeringNavigateState | null,
  // issue #131 / #152: from_pdf_reassign の再 OMR 投入で使う「元タイトル」。 multipart filename
  // (`<元タイトル>.pdf`) と original_title の双方に使う。 完成 PDF 名を渡してはならない。
  originalTitle: string | undefined,
  // issue #152: 再 OMR (from_pdf_reassign) の part。 navState.part 欠落時は stored の meta.part。
  part: FingeringPart | null,
  onProgress?: (progress: JobProgress) => void,
  // issue #148: 完了通知 / 自動保存フラグ / 結果ファイル名 (from_json API に伝搬)。
  notifyOpts?: RegenerateNotifyOptions,
): Promise<RegenerateResult> {
  // issue #32: navigate state から colorMode を抽出 (省略時は score-engine 既定にフォールバック)。
  const colorMode = navState?.colorMode;
  switch (action) {
    case 'pdf': {
      const pdfBlob = await fingeringApi.regeneratePdf(
        originalPdfBlob, fingeringJson, onProgress, colorMode, notifyOpts,
      );
      return { pdfBlob };
    }
    case 'audio': {
      if (!mxlBlob) throw new Error('音源生成に必要な MXL データがありません');
      // issue #91: MP3 名の mode 別命名のため navState.mode を搬送する。
      const { audioBlob, audioFileName } = await fingeringApi.regenerateAudio(
        mxlBlob, fingeringJson, bpm, onProgress, navState?.mode, notifyOpts,
      );
      return { audioBlob, audioFileName };
    }
    case 'both': {
      if (!mxlBlob) throw new Error('再生成に必要な MXL データがありません');
      // issue #91: PDF/MP3 名の mode 別命名のため navState.mode を搬送する。
      const { pdfBlob, audioBlob, audioFileName } = await fingeringApi.regenerateBoth(
        originalPdfBlob, mxlBlob, fingeringJson, bpm, onProgress, colorMode, navState?.mode, notifyOpts,
      );
      return { pdfBlob, audioBlob, audioFileName };
    }
    case 'from_pdf_reassign': {
      // 案 R (issue #8): チューニング変更で編集中運指破棄 → 元 PDF から再 OMR + 再アサイン。
      const result = await fingeringApi.processFromPdf({
        pdfBlob: originalPdfBlob,
        // issue #131 / #152: multipart filename は「元タイトル + .pdf」。 完成 PDF 名を渡すと
        // backend 側の legacy derive でサフィックスが重ねられる (曲名_1st_運指済_1st_運指済)。
        fileName: originalTitle ? `${originalTitle}.pdf` : undefined,
        originalTitle,
        part: part ?? navState?.part ?? 'Guitar1',
        mode: navState?.mode ?? 'part',
        scordatura: navState?.scordatura || undefined,
        bpm,
        colorMode,
        // issue #148: 再 OMR 経路でも保存設定 (通知/自動保存) を honor する (from_pdf の同フィールド)。
        notifyEmail: notifyOpts?.notifyEmail,
        autoSave: notifyOpts?.autoSave,
      }, onProgress);
      return {
        pdfBlob: result.pdfBlob,
        audioBlob: result.audioBlob,
        audioFileName: result.audioFileName,
        fingeringJson: result.fingeringJson,
        mxlBlob: result.mxlBlob,
      };
    }
  }
}

/** PendingAction を jobLifecycleStorage に記録する job_type に対応づける。 */
const PENDING_ACTION_JOB_TYPE: Record<PendingAction, JobType> = {
  pdf: 'from_json_pdf',
  audio: 'from_json_audio',
  both: 'from_json_both',
  from_pdf_reassign: 'from_pdf',
};

// issue #36 M-2 + #72 + #75: reload 復元失敗を errorKind 別の文言で受ける (無言の復元失敗を回避)。
const RESUME_FAILURE_MESSAGES: Record<string, string> = {
  stale: '前回の処理は完了しているか中断されました。 必要であれば、 もう一度楽譜をアップロードしてください。',
  expired: '前回の処理結果は有効期限切れで取得できませんでした。お手数ですが、もう一度楽譜をアップロードしてください。',
  watchdog: '処理が制限時間 (30 分) を超えました。 ファイルが大きい場合はページ数を減らして再投入してください。',
  fetch: '処理は完了しましたが結果取得に失敗しました。 ページを再読込してお試しください。',
};
const RESUME_FAILURE_DEFAULT_MESSAGE =
  '前回の処理結果を復元できませんでした。お手数ですが、もう一度楽譜をアップロードしてください。';

// issue #119: HealthStatusContext が null (Provider 外) のときの markHealthy フォールバック。
// モジュールレベルの安定参照にして handleSubmit の deps を揺らさない。
const noopMarkHealthy = (): void => {};

const FingeringPage: React.FC = () => {
  usePageTitle('運指生成 | おまかせ運指くん');
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  // issue #84: activeJobs.length を ActiveJobsBadge の件数表示に使う。
  // issue #116 FE-2: lastErrorJobId は auto-save ON ジョブ失敗時の ErrorModal トリガ (done 側の鏡像)。
  const { registerJob, activeJobs, lastDoneJobId, lastErrorJobId } = useJobTracker();
  const activeJobsCount = activeJobs.length;
  // issue #79 Phase γ: Drive token silent 取得は Provider 経由 (driveTokenStore 直呼び撤去)。
  const driveAuth = useDriveAuth();
  // issue #115 T3 / #132: score-engine の health を観測し、 error 確定時のみ起動中案内を出す
  //   (pending = loading 中は出さない。 フラッシュ防止、 下の banner 参照)。
  // issue #119: App が張る共有 HealthStatusContext を消費する (useHealthCheck 直呼びをやめ、
  //   recovery poll / held-open ping を App の 1 インスタンスに一本化する)。 Provider 外 (null) は
  //   pending 扱い + noop markHealthy でフォールバックする。
  // issue #115 (cold start): markHealthy は「投入成功 = サーバ稼働」の最強シグナルで共有 health を即
  //   健全化するフック。 これで banner (このページ) と footer pill (AppFooter) が同時に ok へ回復する。
  const health = useHealthStatus();
  const markHealthy = health?.markHealthy ?? noopMarkHealthy;
  const [isProcessing, setIsProcessing] = useState(false);
  // 進行中ジョブの進捗 (queued/processing)。null のとき進捗 UI は非表示。
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultFileName, setResultFileName] = useState<string>('');
  // issue #32 PR2: Download Split Button の選択色 (SSOT)。localStorage から lazy init。
  const [pdfColorMode, setPdfColorMode] = useState<PdfColorMode>(() => getPdfColorMode());
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioFileName, setAudioFileName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // issue #67 Phase G: 自動保存ステータス。 'ok' のとき手動 Drive 保存ボタンを抑止して
  // 「✅ 自動保存済」 表示に切替える。 token_expired / drive_error は通知 + 手動 fallback。
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus | null>(null);
  // issue #154: この結果 (storedSnapshot.fileName) が Drive 保存済みか。 localStorage 永続フラグを
  // SSOT とし、 hydrate / 手動保存 / 自動保存 ok / 陳腐化検知 の 4 経路で更新する。
  // (#142 の manualDriveSaved はメモリのみでリロードで消えていたため、 本 state に一本化した。)
  const [driveSaved, setDriveSaved] = useState(false);
  // issue #111: 自動保存 ON/OFF を state に持ち上げ (AutoSaveToggle/NotifyToggle 連動 + submit ガードの SSOT)。
  const [autoSaveEnabled, setAutoSaveEnabled] = useState<boolean>(() => getAutoSaveEnabled());
  const [detectedBpm, setDetectedBpm] = useState<number | undefined>();
  // fingeringJson 自体を state で保持し、hasFingeringData は derive する (M2)。
  const [fingeringJson, setFingeringJson] = useState<string | null>(null);
  const hasFingeringData = fingeringJson !== null;
  // Drive 保存用: loadFingeringResult の結果スナップショットと、現行 part を保持。
  const [storedSnapshot, setStoredSnapshot] = useState<FingeringStorageResult | null>(null);
  const [currentPart, setCurrentPart] = useState<FingeringPart | null>(null);
  const [currentTitle, setCurrentTitle] = useState<string>('');
  // issue #76 Phase 4-β: 画面非表示中に完了した未通知 done entry を画面復帰時に通知するバナー。
  const [bannerEntry, setBannerEntry] = useState<JobLifecycleEntry | null>(null);
  // issue #85 round 2: fire-and-forget done を即・結果カード表示せず、 完了モーダル経由で
  // [結果を開く] / [閉じる] を選ばせる。 null = 非表示、 順送り (非 null の間は新規モーダル抑止)。
  const [completionModal, setCompletionModal] = useState<{ jobId: string; fileName: string } | null>(null);
  // issue #116 FE-2: auto-save ON ジョブ失敗時の ErrorModal (完了モーダルの鏡像)。 null = 非表示。
  // 順送り (setErrorModal((prev) => prev ?? …)) で多重表示を抑止する。
  const [errorModal, setErrorModal] = useState<{ jobId: string; errorKind?: string; errorMessage?: string } | null>(null);
  // issue #116 FE-2: 失敗ジョブ再投入でフォームへ復元する内容。 restoreNonce は同一内容でも
  // 再適用できるよう再投入のたびに bump する (FingeringForm の復元 useEffect のトリガ)。
  const [restoreRequest, setRestoreRequest] = useState<FingeringRestoreRequest | null>(null);
  const [restoreNonce, setRestoreNonce] = useState(0);
  // issue #86: DriveHistoryModal の open 状態を DriveFooterBar からリフト。
  // バナー CTA / 件数バッジ / フッタ「📚 履歴」ボタンのどこからでも開けるようにする。
  const [historyOpen, setHistoryOpen] = useState(false);
  const processedRequestRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  // issue #85 round 3 (#42): 「結果を開く」後にメイン画面をトップへ戻すためのスクロールコンテナ。
  // 最外 div (`fixed inset-0 overflow-y-auto`) がスクロールコンテナなので、 window でなく
  // この div の scrollTo(0,0) を呼ぶ。
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isConfigured: isDriveConfigured, saveFingering } = useGoogleDriveSaver();
  // 保存先フォルダの SSOT。localStorage から初期化し、フッタの保存先変更で更新する。
  const [driveTarget, setDriveTarget] = useState<DriveTargetFolder | null>(() => getSavedFolderTarget());
  const addToHomePrompt = useAddToHomePrompt(hasFingeringData);
  // リロード/タブ切替後の進行中ジョブ復元。復元 done で IndexedDB に結果が保存される。
  const resume = useJobLifecycleResume();

  const handleDriveFolderChanged = useCallback((folder: DriveTargetFolder) => {
    setDriveTarget(folder);
  }, []);

  // IndexedDB に保存済みの結果を読み込み、結果カードへ反映する。
  // 初回マウントと、リロード復元 (useJobLifecycleResume) の done 後の両方から呼ぶ。
  const loadAndShowResult = useCallback(async () => {
    const stored = await loadFingeringResult();
    if (!stored || !isMountedRef.current) return;

    const url = URL.createObjectURL(stored.blob);
    setResultUrl(url);
    setResultFileName(stored.fileName);
    if (features.enableAudio && stored.audioBlob) {
      const aUrl = URL.createObjectURL(stored.audioBlob);
      setAudioUrl(aUrl);
      setAudioFileName(stored.audioFileName || 'audio.mp3');
    }
    if (stored.detectedBpm !== undefined) {
      setDetectedBpm(stored.detectedBpm);
    }
    if (stored.fingeringJson) {
      setFingeringJson(stored.fingeringJson);
    }
    setStoredSnapshot(stored);
  }, []);

  // issue #154: 結果スナップショットが差し替わるたびに localStorage の保存済みフラグから復元する。
  // これでリロード後も「Drive保存済み ✓」が維持される (旧 manualDriveSaved はメモリのみで消えていた)。
  const storedFileName = storedSnapshot?.fileName ?? '';
  useEffect(() => {
    setDriveSaved(isDriveSaved(storedFileName));
  }, [storedFileName]);

  // 進行中 job が無い場合のみ mount 時に IndexedDB から hydrate する (issue #7/#9 reopen 案 H)。
  // 進行中 job がある場合は resume.restored 経由で結果カードに反映される (issue #7 経路維持)。
  // hasActiveResume を boolean 化して deps に入れる: entry オブジェクト参照変化での余分発火を回避。
  // hasPendingSubmit: pre-jobID 区間 (POST 中で jobId 未確定) reload の race 防御 (issue #9 reopen)。
  const hasActiveResume = resume.entry !== null;
  const hasPendingSubmit = resume.hasPendingSubmit;
  useEffect(() => {
    if (!hasActiveResume && !hasPendingSubmit) {
      loadAndShowResult();
    }
  }, [loadAndShowResult, hasActiveResume, hasPendingSubmit]);

  // issue #76 Phase 4-β: 画面非表示中に完了した未通知 done entry を mount + visibility=visible で確認し、
  // 最新 1 件を CompletionBanner として表示する。
  useEffect(() => {
    const check = () => {
      const list = listEntriesNeedingReturnNotification();
      setBannerEntry(list[0] ?? null);
    };
    check();
    const handler = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  const handleDismissBanner = useCallback(() => {
    if (bannerEntry) {
      markEntryNotified(bannerEntry.jobId);
      setBannerEntry(null);
    }
    // issue #84 提案C (差し替え可能): 完了バナーの既読を履歴の新着既読と連動させ二重通知を避ける。
    // この行を削除すれば提案C を外せる。
    setHistoryLastSeen();
  }, [bannerEntry]);
  const handleOpenHistoryFromBanner = useCallback(() => {
    if (bannerEntry) {
      markEntryNotified(bannerEntry.jobId);
      setBannerEntry(null);
    }
    // issue #86: バナー CTA で履歴 modal を開く (従来はマーク+非表示のみで未配線だった)。
    setHistoryOpen(true);
  }, [bannerEntry]);

  // issue #85: fire-and-forget の done job を結果カードへ自動復元する共通アクション。
  // 編集中ガード (Q1 候補A) = この関数は FingeringPage がアクティブ表示中 (= /fingering route が
  // mount 中) の useEffect からのみ呼ばれる。エディタ /fingering/editor 表示中は構造的に走らない。
  const autoRestoredRef = useRef<Set<string>>(new Set());
  const autoRestore = useCallback(async (jobId: string) => {
    if (autoRestoredRef.current.has(jobId)) return; // 同一 jobId 去重 (in-memory)
    const entry = loadJobLifecycleEntry(jobId);
    // issue #92: entry が既に無い = 後始末済み (成功 clear / 404 gone / stale 削除のいずれか)。
    // 去重 ref は再 mount で消えるが、 localStorage の entry 消失は残るため、 これで
    // 完了ジョブ 1 件につき result 取得を最大 1 回に保証する (404 連発の永続防止)。
    if (entry === null) {
      autoRestoredRef.current.add(jobId);
      return;
    }
    autoRestoredRef.current.add(jobId);
    // issue #126: 既に IndexedDB 復元済み (前回 mount で restored マーク済)。 再 getJobResult /
    // 再保存はせず (元 PDF の破壊的上書きを封じる)、 未通知なら完了モーダルだけ出す。
    if (entry.restored) {
      const fileName = entry.params?.fileName ?? '運指結果.pdf';
      setCompletionModal((prev) => prev ?? { jobId, fileName });
      setBannerEntry(null);
      return;
    }
    const outcome = await restoreJobResultCore(jobId, entry);
    if (outcome === 'gone') {
      // issue #92: 404 (result 恒久消失)。 done entry を後始末して 404 連発を止める。
      // 去重 ref は解除しない (恒久失敗なので再 mount / visibility でも再試行させない)。
      markEntryNotified(jobId);
      clearJobLifecycleEntry(jobId);
      setBannerEntry(null);
      return;
    }
    if (outcome === 'transient-fail' || !isMountedRef.current) {
      // 一時失敗は静かにフォールバック (CompletionBanner / 履歴へ委譲)。去重を解除して再試行余地を残す。
      autoRestoredRef.current.delete(jobId);
      return;
    }
    // issue #85 round 2 (案A): 'ok' = restoreJobResultCore で IndexedDB 保存済み。
    // 即・結果カード反映 (loadAndShowResult) せず、 完了モーダルを出して [結果を開く]/[閉じる] を
    // 選ばせる。 反映・既読化・entry clear はモーダル操作 (下記ハンドラ) 側に移す。
    // R2-Q5 順送り: 既にモーダル表示中なら上書きしない (この entry は残り、 閉じた後に再度拾われる)。
    // R2-Q4: モーダル優先で CompletionBanner を抑止。
    const fileName = entry.params?.fileName ?? '運指結果.pdf';
    setCompletionModal((prev) => prev ?? { jobId, fileName });
    setBannerEntry(null);
  }, []);

  // issue #85 round 2: 完了モーダル「結果を開く」。 IndexedDB の結果を結果カードへ反映し、
  // entry を後始末 (既読化 + clear + 履歴既読) してモーダルを閉じる。
  // issue #85 round 3 (#42): 「結果を開く」後はメインページを開き直した相当にする。
  // 開いている overlay (履歴 modal / 完了バナー / AddToHome) を全閉じし、 スクロールをトップへ戻す。
  const handleOpenResultFromModal = useCallback(async () => {
    const jobId = completionModal?.jobId;
    setCompletionModal(null);
    // round 3: メイン画面リセット (overlay 全閉じ + スクロールトップ)。
    setHistoryOpen(false);
    setBannerEntry(null);
    // 結果を見たいだけなので AddToHome の hide 設定は変えない (rememberHide=false)。
    addToHomePrompt.close(false);
    scrollContainerRef.current?.scrollTo({ top: 0, left: 0 });
    if (!jobId) return;
    await loadAndShowResult();
    finalizeCompletedJob(jobId);
  }, [completionModal, loadAndShowResult, addToHomePrompt]);

  // issue #85 round 2: 完了モーダル「閉じる」(R2-Q3 案I)。 反映せず entry を後始末し、
  // 履歴新着マーク (#84) へフォールバックする。 done entry は clear (二重通知防止)。
  const handleCloseCompletionModal = useCallback(() => {
    const jobId = completionModal?.jobId;
    setCompletionModal(null);
    if (!jobId) return;
    finalizeCompletedJob(jobId);
  }, [completionModal]);

  // リアルタイム経路: 表示中に done 通知が来たら即復元 (Q2: 閲覧中の上書き許容)。
  useEffect(() => {
    if (lastDoneJobId) {
      void autoRestore(lastDoneJobId);
    }
  }, [lastDoneJobId, autoRestore]);

  // issue #116 FE-2: 失敗ジョブを「メイン画面のフォームへ復元」する共通アクション。
  // ErrorModal [再投入] と DriveFooterBar 履歴の [再投入] の両方から呼ぶ。 抽出済み PDF が
  // 30分 TTL 切れなら再アップロード誘導 toast。 復元成功時は overlay を閉じてフォームへスクロール
  // (完了モーダル「結果を開く」の鏡像)。 旧失敗 entry は温存する (retryFailedJob 側で clear しない)。
  const handleRestoreToForm = useCallback(async (jobId: string) => {
    const outcome = await retryFailedJob(jobId);
    if (!isMountedRef.current) return;
    // 成否いずれも overlay (エラーモーダル / 履歴) を閉じてトップのフォームへスクロールし、
    // ユーザーが復元 or 再アップロードにすぐ着手できる状態にする。
    setErrorModal(null);
    setHistoryOpen(false);
    scrollContainerRef.current?.scrollTo({ top: 0, left: 0 });
    if (outcome.outcome === 'expired') {
      // 抽出済み PDF が 30分 TTL 切れ → 復元不可。 トップから再アップロードを促す。
      showToast(
        '元 PDF の保持期限 (30分) が切れています。トップから再アップロードしてください',
        { kind: 'info' },
      );
      return;
    }
    // フォームへ復元 (nonce を bump して同一内容でも再適用可能に)。
    setRestoreRequest(outcome.restore);
    setRestoreNonce((n) => n + 1);
  }, [showToast]);

  // issue #116 FE-2: ErrorModal [再投入] → restore-to-form。
  const handleRetryFromErrorModal = useCallback(() => {
    const jobId = errorModal?.jobId;
    if (!jobId) return;
    void handleRestoreToForm(jobId);
  }, [errorModal, handleRestoreToForm]);

  // issue #116 FE-2: ErrorModal [閉じる] → モーダルを閉じるのみ。 失敗 entry は履歴に残し、
  // ユーザーはフッタ「📚 履歴」からいつでも再投入できる (履歴フォールバック)。
  const handleCloseErrorModal = useCallback(() => {
    setErrorModal(null);
  }, []);

  // issue #116 FE-2: 一度表示 (= dismiss 可能に) した lastErrorJobId を記録し、 同じ jobId で
  // 二度と自動表示しないためのガード。 これがないと、 閉じた後に autoSaveEnabled トグル (OFF→ON) で
  // effect が再実行され prev===null のため ErrorModal が復活してしまう。 lastErrorJobId は失敗確定の
  // たびに一意な jobId が 1 回 push されるだけ (過去 jobId へ戻らない) ため、 直近に出した jobId 1 つを
  // 覚えれば十分 (Set は不要)。
  const shownErrorJobIdRef = useRef<string | null>(null);
  // issue #116 FE-2: auto-save ON (fire-and-forget) ジョブ失敗時に ErrorModal を出す (done 側の鏡像)。
  // lastErrorJobId は ON 経路の失敗確定でのみ発火するが、 二重防御として autoSaveEnabled でも
  // ゲートする (OFF/同期経路は既存の setError インライン表示を維持し、 モーダルは出さない)。
  // 順送り: 既に表示中なら上書きしない (prev ?? …)。
  useEffect(() => {
    if (!lastErrorJobId) return;
    if (!autoSaveEnabled) return;
    // 同じ失敗を一度出したら (閉じた後も) 二度と自動表示しない。
    if (shownErrorJobIdRef.current === lastErrorJobId) return;
    shownErrorJobIdRef.current = lastErrorJobId;
    const entry = loadJobLifecycleEntry(lastErrorJobId);
    setErrorModal((prev) => prev ?? {
      jobId: lastErrorJobId,
      errorKind: entry?.errorKind,
      errorMessage: entry?.errorMessage,
    });
  }, [lastErrorJobId, autoSaveEnabled]);

  // キャッチアップ経路: mount + visibility=visible で done entry (最新 1 件のみ、Q3) を復元。
  // エディタから戻った / タブ復帰 / mount 時に既に done entry がある場合をカバー。
  useEffect(() => {
    const check = () => {
      const list = listDoneEntriesForAutoRestore();
      const target = list[0];
      if (!target) return;
      // issue #92 G-1 (必須): finishedAt が 30 分超過の done entry は result が既に消えているため、
      // getJobResult を一切叩かず即削除する (完了ジョブ 1 件につき result 取得は最大 1 回)。
      if (
        typeof target.finishedAt === 'number' &&
        Date.now() - target.finishedAt > JOB_LIFECYCLE_TIMEOUT_MS
      ) {
        markEntryNotified(target.jobId);
        clearJobLifecycleEntry(target.jobId);
        return;
      }
      void autoRestore(target.jobId);
    };
    check();
    const handler = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [autoRestore]);

  // リロード復元したジョブが done になり saveFingeringResult が完了したら結果カードへ反映する。
  // mount 時の hydration は上の useEffect で resume.entry === null の場合のみ実施。
  useEffect(() => {
    if (resume.restored) {
      loadAndShowResult();
    }
  }, [resume.restored, loadAndShowResult]);

  // issue #24 Bug A: 履歴復元 (Drive .omfz) 完了後、 navigate state.restored を契機に
  // loadAndShowResult を再発火する。 同 pathname の navigate では React Router が再 mount しないため、
  // useEffect を location.state 変化で再評価する。 pendingAction と同時にある場合は pendingAction 経路優先 (排他制御 R-1)。
  const processedRestoreRef = useRef<number | null>(null);
  useEffect(() => {
    const navState = location.state as FingeringNavigateState | null;
    if (!navState?.restored) return;
    if (navState.pendingAction) return;
    if (navState.restoredAt === undefined) return;
    if (processedRestoreRef.current === navState.restoredAt) return;
    processedRestoreRef.current = navState.restoredAt;
    loadAndShowResult();
    // issue #177: 復元は定義上 Drive 保存済み。 storedFileName 不変の再復元 (同一結果) でも
    // 確実にリンクコピーを活性化させるためダメ押しで立てる (hook 側 markDriveSaved と両立)。
    setDriveSaved(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, loadAndShowResult, navigate]);

  // reload 復元中の進行中状態。 resuming かつエラーなしの間は処理中扱い。
  // issue #42: status=done 早出しによる空白ギャップ防止のため `resume.status !== 'done'` は撤去。
  //   useJobStatusPolling.setStatus('done') が flush されると status は即 'done' になるが、
  //   onDone コールバックの handleDone (getJobResult + saveFingeringResult) はまだ await 中。
  //   resume.resuming は handleDone 完走の finally で false になるため、 これを信頼することで
  //   スピナー消失 → 結果カード表示の間の空白窓を構造的に排除する。
  // hasPendingSubmit: pre-jobID 区間 reload では jobId 不明でも「処理中…」スピナーを出す。
  const isResumeInProgress =
    (resume.resuming && resume.error === null)
    || hasPendingSubmit;

  // issue #36 M-2 + #72 + #75: F-1/F-2 を入れても reload 復元はゼロにできない。 復元失敗を無言にせず、
  // errorKind ごとに原因が伝わる文言と再投入導線で受ける。
  // resume.error はローカル error state (handleSubmit / 再生成の失敗) とは別経路。
  const resumeFailureMessage = resume.error
    ? RESUME_FAILURE_MESSAGES[resume.errorKind ?? ''] ?? RESUME_FAILURE_DEFAULT_MESSAGE
    : null;

  // === issue #2: pre-jobID オーバーレイ＋状態表示集約 ===
  // pre-jobID 区間 (POST /jobs 中で jobId 未取得) は SubmittingOverlay と
  // beforeunload ガードで reload を抑止する (設計 §3, §4.4)。
  const isSubmitting = isProcessing && !jobProgress;
  useBeforeUnloadGuard(isSubmitting);

  // FingeringForm スピナーを表示する条件: ローカル投入中 OR reload 復元中
  const showFormSpinner = isProcessing || isResumeInProgress;

  // 現在の job ステータス (queued/processing) を jobProgress と resume の両方から拾う
  const currentJobStatus: 'queued' | 'processing' | undefined =
    jobProgress?.status === 'queued' || jobProgress?.status === 'processing'
      ? jobProgress.status
      : resume.status === 'queued' || resume.status === 'processing'
        ? resume.status
        : undefined;

  // FingeringForm に渡す文言。queued は待ち時間ヒント、それ以外は汎用の処理中文言。
  const processingMessage =
    currentJobStatus === 'queued'
      ? '順番待ち中 — いつもより時間がかかるかもしれません'
      : '処理中…お待ちください';

  useEffect(() => {
    return () => {
      if (resultUrl) URL.revokeObjectURL(resultUrl);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [resultUrl, audioUrl]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // issue #126: 先行 PDF 削除を完了モーダル確定へ後ろ倒しした分、 モーダル未操作で滞留した
  // pending-pdfs を TTL (30 分) で掃除する。 mount 時に 1 回だけ best-effort で走らせる。
  // IndexedDB 不在環境等の reject は握りつぶす (掃除失敗で投入フローを止めない)。
  useEffect(() => {
    void (async () => {
      try {
        await purgeExpiredPendingOriginalPdfs();
      } catch {
        // best-effort: 掃除失敗は無視 (IndexedDB 不在環境等)。
      }
    })();
  }, []);

  useEffect(() => {
    const navState = location.state as FingeringNavigateState | null;
    // pendingAction / requestedAt は issue #24 で optional 化したため、 local const で narrow する。
    if (!navState?.pendingAction || navState.requestedAt === undefined) return;
    // 以降 closure に渡すため definite な PendingAction として再代入 (async function 内では control-flow narrowing が失われる)。
    const pendingAction: PendingAction = navState.pendingAction;
    const requestedAt = navState.requestedAt;
    if (processedRequestRef.current === requestedAt) return;
    processedRequestRef.current = requestedAt;

    async function processPendingAction() {
      if (!navState) return;
      setIsProcessing(true);
      setError(null);
      setJobProgress(null);
      // issue #148: 再生成のたびに自動保存ステータスを初期化する (前回の 'ok' 表示を持ち越さない)。
      setAutoSaveStatus(null);

      // 再生成ジョブの jobId。進捗通知の初回から確定し、done 正常完了時の掃除に使う。
      let activeJobId: string | null = null;
      // done で正常 return できたか。 throw 時は entry を残置する (issue #36 F-2b)。
      let completedSuccessfully = false;
      const jobType = PENDING_ACTION_JOB_TYPE[pendingAction];

      // pre-jobID 区間 reload race 防御 (issue #9 reopen、handleSubmit と同パターン)。
      // 再生成経路も dispatchRegenerate 内で POST → jobId 受領を経るため同じ race が発生する。
      savePendingSubmitMarker();

      try {
        const stored = await loadFingeringResult();
        if (!stored?.fingeringJson || !stored?.originalPdfBlob) {
          if (!isMountedRef.current) return;
          setError('再生成に必要なデータが見つかりません。エディタからやり直してください。');
          return;
        }

        // issue #154: 再生成結果はまだ Drive 未保存 (中身が変わる)。 古いフラグを落として
        // 「保存済み」誤表示 = 古い PDF の URL を配ることを防ぐ。
        // issue #177: 中身が変わるので古い fileId 控えも捨てる (陳腐化した PDF を指さないため)。
        clearDriveSaved(stored.fileName);
        clearDrivePdfFileId(stored.fileName);
        if (isMountedRef.current) setDriveSaved(false);

        // issue #152: 命名ソースを「元タイトル」に統一する。 stored.originalTitle が正で、
        // 欠落する旧データは meta.title / stored.fileName から derive で救済する (自己治癒)。
        // part / mode も (元タイトル, part, mode) 純関数の引数として確定させ、 結果名を再計算する
        // (旧・汚染名 `曲名_1st_運指済_1st_運指済.pdf` はここで正規名へ収束する)。
        const storedMeta = readStoredMeta(stored.fingeringJson);
        const originalTitle = resolveOriginalTitle({
          originalTitle: stored.originalTitle,
          metaTitle: storedMeta.title,
          fileName: stored.fileName,
        });
        const regenPart = navState.part ?? toFingeringPart(storedMeta.part);
        const regenMode: FingeringMode = navState.mode ?? stored.mode ?? 'part';
        const fileName = buildOutputFileName(originalTitle, regenPart ?? 'Guitar1', regenMode);
        // 解決不能な旧データは空文字。 API / 永続化には「未指定」として undefined を渡す。
        const originalTitleOpt = originalTitle || undefined;

        // issue #148: エディタ「保存設定」 (localStorage SSOT) を再生成にも反映する。
        // handleSubmit と同じ resolveNotifyEmail で判定 (自動保存 OFF なら通知も送らない二重防御)。
        const autoSave = getAutoSaveEnabled();
        const notifyEmail = resolveNotifyEmail(autoSave);
        const notifyOpts: RegenerateNotifyOptions = {
          notifyEmail,
          autoSave,
          fileName,
          // issue #152: backend も (元タイトル, part, mode) で MP3 / omfz 名を組む。
          originalTitle: originalTitleOpt,
        };

        const result = await dispatchRegenerate(
          pendingAction,
          stored.fingeringJson,
          stored.originalPdfBlob,
          stored.mxlBlob ?? null,
          navState.currentBpm,
          navState,
          // issue #131 / #152: 再 OMR 投入の multipart filename / original_title の元になる元タイトル。
          originalTitleOpt,
          regenPart,
          (progress) => {
            if (!isMountedRef.current) return;
            activeJobId = progress.jobId;
            setJobProgress(progress);
            // 再生成時も結果名 (再計算済み) を params で引き継ぐ (async reload F3)。
            // issue #107: mode も params に載せ、reload resume 後も Drive 保存ボタンを再 enable できるようにする。
            // issue #152: originalTitle も載せ、 reload 復元後の再生成でも命名ソースを保つ。
            saveJobLifecycleEntry({
              jobId: progress.jobId,
              jobType,
              status: progress.status,
              createdAt: Date.now(),
              params: {
                fileName,
                originalTitle: originalTitleOpt,
                part: regenPart ?? undefined,
                mode: regenMode,
              },
            });
            // entry → マーカー削除の順序 (spec §5-3)。
            clearPendingSubmitMarker();
          },
          // issue #148: 保存設定 (通知/自動保存) を from_json API に伝搬する。
          notifyOpts,
        );
        if (!isMountedRef.current) return;

        await saveFingeringResult({
          pdfBlob: result.pdfBlob ?? stored.blob,
          fileName,
          originalTitle: originalTitleOpt,
          audioBlob: result.audioBlob,
          audioFileName: result.audioFileName,
          // from_pdf_reassign 経路は再 OMR で fingeringJson / mxl が刷新されるので新規結果を採用。
          fingeringJson: result.fingeringJson ?? stored.fingeringJson,
          originalPdfBlob: stored.originalPdfBlob,
          mxlBlob: result.mxlBlob ?? stored.mxlBlob ?? undefined,
          detectedBpm: stored.detectedBpm,
          // issue #81: 再生成後も Drive 保存ボタンを enable に保つため mode を永続化する。
          // issue #152: navState.mode 欠落時は stored.mode を維持する (mode 喪失で命名がぶれるのを防ぐ)。
          mode: regenMode,
        });
        if (!isMountedRef.current) return;
        const refreshed = await loadFingeringResult();
        if (isMountedRef.current && refreshed) setStoredSnapshot(refreshed);
        // 古い resultUrl / audioUrl は [resultUrl, audioUrl] deps の cleanup useEffect が
        // 値変化時に revoke するため、ここでの明示 revoke は不要 (二重 revoke 回避)。
        if (result.pdfBlob) {
          setResultUrl(URL.createObjectURL(result.pdfBlob));
          setResultFileName(fileName);
        }
        if (result.audioBlob && result.audioFileName) {
          setAudioUrl(URL.createObjectURL(result.audioBlob));
          setAudioFileName(result.audioFileName);
        }

        // issue #148: 自動保存 ON + Drive 設定済みなら frontend 側で Drive 自動保存する
        // (from_json 再生成は backend 自動保存非関与のため、 手動保存と同じ saveFingering 経路を使う)。
        // silent token が取れたときだけ実行し、 未ログイン / 失敗時は手動ボタンを残す (all-or-nothing)。
        if (autoSave && isDriveConfigured && refreshed) {
          try {
            const token = await driveAuth.getSilentToken();
            if (token) {
              const savedTarget = getSavedFolderTarget();
              await saveFingering(refreshed, {
                // issue #152: title は「元タイトル」契約。 完成 PDF 名を渡すと「_運指済」が
                // 二重付与される。 part も解決済みの値を渡し、 omfz 名が手動保存と一致するよう揃える
                // (part 欠落だと総譜でも part infix が落ち、 同一結果の omfz が別名で増殖する)。
                title: originalTitle,
                part: toPartLabel(regenPart),
                mode: regenMode,
                parentFolderId: savedTarget?.id,
              });
              // issue #154: saveFingering 内で markDriveSaved 済み。 表示 state も同期する。
              if (isMountedRef.current) {
                setAutoSaveStatus('ok');
                setDriveSaved(true);
              }
            }
          } catch {
            // silent 不可 / 保存失敗は手動ボタン維持 (autoSaveStatus は null のまま)。
          }
        }

        // ここまで到達 = done 正常完了。 finally での entry 掃除を許可する。
        completedSuccessfully = true;
      } catch (err) {
        if (!isMountedRef.current) return;
        // issue #76 Phase 5-β: ProcessingError + errorKind は共有定数経由で文言構築。
        if (err instanceof ProcessingError && err.errorKind && getFingeringErrorDetail(err.errorKind)) {
          setError(buildFingeringErrorMessage(err.errorKind, err.message));
        } else {
          const msg = err instanceof Error ? err.message : String(err);
          setError(`再生成に失敗しました: ${msg}`);
        }
      } finally {
        // entry 掃除は done 正常完了時のみ。 throw 時は entry を残し reload 後 resume に委ねる (issue #36 F-2b)。
        if (activeJobId && completedSuccessfully) clearJobLifecycleEntry(activeJobId);
        // POST 同期エラー (jobId 未受領) でも投入中マーカーを掃除する (issue #9 reopen)。
        clearPendingSubmitMarker();
        if (isMountedRef.current) {
          setJobProgress(null);
          setIsProcessing(false);
        }
        navigate(location.pathname, { replace: true, state: null });
      }
    }

    processPendingAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSubmit = useCallback(async (
    // issue #113 案C: FingeringForm で選択ページを抽出済みの PDF Blob。全選択/空は元 file 相当。
    pdfBlob: Blob,
    originalFileName: string,
    part: FingeringPart,
    mode: FingeringMode,
    scordatura?: string,
    bpm?: number,
  ) => {
    // issue #137: 通知 ON かつ宛先メアド空欄なら投入をブロックする (通知先が無いのに通知 ON の不整合防止)。
    // #111 二重防御: 自動保存 OFF 時は通知メール自体を送らないため、 その場合はブロックしない
    // (autoSaveEnabled でガードを閉じることで NotifyToggle disabled の見え方と一致させる)。
    if (autoSaveEnabled) {
      const notifyError = validateNotifySettings({
        enabled: getNotifyEnabled(),
        email: getNotifyEmail(),
      });
      if (notifyError) {
        setError(notifyError);
        // issue #143: false を返して FingeringForm に投入ブロックを伝える。
        // フォーム側は #114 resetForm を抑止し、 入力 (file/設定) を保持する。
        return false;
      }
    }
    setError(null);
    setJobProgress(null);
    setAutoSaveStatus(null);
    // issue #142/#154: 新規投入で Drive 保存はリセット。 共有リンクコピーボタンを再び非活性へ。
    setDriveSaved(false);
    // issue #111: 自動保存設定は state (autoSaveEnabled) を SSOT とする。 useCallback closure が
    // 投入処理中の値を固定するため、 await を跨いでも揺れない (旧: getAutoSaveEnabled() 都度読み)。
    // issue #98/#111: 完了メール通知の宛先。 通知 ON + 簡易バリデーション通過 + 自動保存 ON のときだけ送る
    // (opt-in / 二重防御)。 不正・空メアド or 通知 OFF or 自動保存 OFF → undefined → FormData に積まれない。
    const notifyEmail = resolveNotifyEmail(autoSaveEnabled);
    // issue #97: SubmittingOverlay (ジョブ投入中…) を ON/OFF 共通で表示する。
    // #89(21bfca3) で setIsProcessing(true) が if(!autoSaveEnabled) 内へ移り ON 経路から
    // 消えていた regression を、冒頭の無条件実行へ戻す (fe7e0d1 以前の挙動)。
    // 解放は ON 成功 (:setIsProcessing(false))/ catch / OFF finally が担う。
    setIsProcessing(true);
    // issue #99: 投入時に前回の結果カード state 一式をクリアする (ON/OFF 共通)。
    // #89 の「ON は前回結果を温存」方針は #99 で撤回 (投入したら ON でも消す)。
    // 結果カード描画条件 (resultUrl && !isProcessing) の主条件 resultUrl を消し、
    // jobId 受領で isProcessing=false に戻っても前回結果が復活しないようにする。
    // ※ setIsProcessing(true) は #97 で復活済み (この行の直前)。
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setResultUrl(null);
    setAudioUrl(null);
    setFingeringJson(null);
    setResultFileName('');
    setAudioFileName('');
    setDetectedBpm(undefined);
    setStoredSnapshot(null);
    setCurrentPart(null);
    setCurrentTitle('');
    // autoSaveStatus は上の setAutoSaveStatus(null) で投入時に null 化済み。
    // IndexedDB results['latest'] も消す。best-effort (失敗しても投入は続行)。
    // ※ pending-pdfs / job-lifecycle は触らない (他ジョブの復元/追跡を壊さないため)。
    try {
      await clearFingeringResult();
    } catch {
      // クリア失敗は無視して投入続行 (console.log 禁止のため静かに握る)。
    }

    // 投入されたジョブの jobId。進捗通知の初回から確定し、done 正常完了時の掃除に使う。
    let activeJobId: string | null = null;
    // done で正常 return できたか。 throw 時 (= done 未確定) は entry を残して
    // reload 後 resume が再挑戦できるようにする (issue #36 F-2b)。
    let completedSuccessfully = false;
    // issue #152: 元タイトル (= アップロード名から .pdf を除いたもの) を投入時に確定し、 以降の
    // 命名 (PDF/MP3/omfz) はすべてこれを起点にする。 完成ファイル名を命名ソースに使わない。
    const originalTitle = toOriginalTitle(originalFileName);
    // 結果ファイル名は投入前に確定する。reload 復元時に params 経由で持ち越す (async reload F3)。
    const fileName = buildOutputFileName(originalTitle, part, mode);
    // issue #154: 同名で再生成した結果はまだ Drive に上がっていない (内容が変わる)。 前回のフラグを
    // 持ち越すと古い PDF の URL を「今の結果」として配ってしまうためクリアする。
    // issue #177: 古い fileId 控えも捨てる (再生成後は中身が変わり陳腐化するため)。
    clearDriveSaved(fileName);
    clearDrivePdfFileId(fileName);

    // issue #67 Phase G: 自動保存 ON のとき silent token 取得 + 保存先フォルダ確保。
    // 保存先はフッタ指定 (driveTargetFolder) を最優先し、 未指定時のみ ensureAppFolder
    // (固定 APP_FOLDER) にフォールバックする (issue #70 hotfix、 手動保存と挙動を揃える)。
    // silent 失敗 (未ログイン / consent 必要) は throw を捕まえて token なしで送信 (silent skip)。
    let driveAccessToken: string | undefined;
    let driveSaveTarget: DriveSaveTarget | undefined;
    if (autoSaveEnabled) {
      try {
        // issue #79 Phase γ: combined silent で readonly + file を同時取得し、 履歴閲覧時の再 consent を防ぐ。
        // useDriveAuth().getSilentToken は内部で getCombinedDriveToken({ interactive: false }) を呼ぶ。
        // silent 失敗時の throw は外側 catch で token=undefined にして silent skip。
        driveAccessToken = await driveAuth.getSilentToken();
        if (driveAccessToken) {
          const savedTarget = getSavedFolderTarget();
          driveSaveTarget = savedTarget ?? {
            id: await ensureAppFolder({ token: driveAccessToken }),
            name: 'APP_FOLDER',
          };
        }
      } catch {
        // silent skip: 未ログイン / popup 必要等は token なし送信に fallback (all-or-nothing)。
        driveAccessToken = undefined;
        driveSaveTarget = undefined;
      }
    }

    // issue #76 Phase 3 sub-I: ON 経路は fire-and-forget。 POST のみ実行し
    // jobId 受領後すぐ UI を解放 (setIsProcessing(false))、 singleton polling worker
    // (jobTracker) へ jobId を登録して完走を委ねる。
    if (autoSaveEnabled) {
      savePendingSubmitMarker();
      try {
        const { jobId } = await fingeringApi.submitFromPdf({
          pdfBlob,
          // issue #131: multipart filename を実名にする (抽出済み PDF が name 無し Blob でも
          // "blob" に化けさせず、 backend 自動保存が blob.omfz / blob.pdf を採用するのを防ぐ)。
          fileName: originalFileName,
          // issue #152: backend も (元タイトル, part, mode) の純関数で命名する。
          originalTitle,
          part,
          mode,
          scordatura,
          bpm,
          colorMode: pdfColorMode,
          driveAccessToken,
          driveParentFolderId: driveSaveTarget?.id,
          notifyEmail,
          // issue #111: backend on_done ガード用に自動保存フラグを送る。
          autoSave: autoSaveEnabled,
        });
        // issue #115 (cold start): jobId 受領 = サーバ稼働の最強シグナル。 health を即健全化し、
        // error 固着した「サーバ起動中」banner を消す (recovery poll を待たず即回復)。
        markHealthy();
        // 抽出済み PDF を先行保存 (reload 復元で originalPdfBlob を再生する基盤)。
        // issue #126: registerJob (完走委譲) より前に書込を await 確定し、 高速完了ジョブの復元が
        // 未コミット中に走る書込レースを解消する (fire-and-forget 撤廃)。
        await savePendingOriginalPdf(jobId, pdfBlob);
        // entry を書込 + driveSaveTarget を記録 (singleton 側の triggerDriveSave が使う)。
        saveJobLifecycleEntry({
          jobId,
          jobType: 'from_pdf',
          status: 'queued',
          createdAt: Date.now(),
          // issue #110: 再投入で元設定を忠実再現するため拡張 params も保存する。
          // issue #152: originalTitle (命名ソース) も保存し、 reload 復元後の再生成でも同名を再現する。
          params: {
            fileName, originalTitle, part, mode, fileSizeBytes: pdfBlob.size,
            scordatura, bpm, colorMode: pdfColorMode,
          },
          driveSaveTarget,
        });
        clearPendingSubmitMarker();
        // singleton 登録 — watchdog 30 分タイムアウトで救済する (#72)。
        registerJob(jobId, { deadlineAt: Date.now() + JOB_LIFECYCLE_TIMEOUT_MS });
        // UI を解放 + toast 通知。
        setIsProcessing(false);
        showToast(
          '✅ ジョブを開始しました。フッタ「📚 履歴」から進捗を確認できます',
          { kind: 'success', durationMs: 5000 },
        );
      } catch (err) {
        clearPendingSubmitMarker();
        setError(resolveSubmitError(err, '投入に失敗しました'));
        setIsProcessing(false);
      }
      return;
    }

    // pre-jobID 区間 (POST /jobs 中で jobId 未確定) reload race 防御 (issue #9 reopen)。
    // POST 直前にマーカーを書き、onProgress (jobId 受領) で entry 書込後にクリアする。
    savePendingSubmitMarker();

    try {
      // File は Blob を継承しているため、そのまま processFromPdf に渡せる。
      // onProgress で進捗を UI に反映し、進行中ジョブを jobLifecycleStorage に記録する
      // (リロード復元の基盤、設計 §0)。params.fileName は handleDone が結果名に使う。
      const result = await fingeringApi.processFromPdf(
        // colorMode は localStorage SSOT (pdfColorMode) を初回生成にも反映する (issue #32)。
        // issue #67: driveAccessToken + driveParentFolderId が undefined なら backend 自動保存は skip。
        // issue #113 案C: 選択ページは FingeringForm で抽出済み (page_range は送らない)。
        {
          // issue #131: multipart filename を実名にする (name 無し Blob の "blob" 化を防ぐ)。
          // issue #152: originalTitle を命名ソースとして送る (backend も同じ純関数で命名する)。
          pdfBlob, fileName: originalFileName, originalTitle, part, mode, scordatura, bpm,
          colorMode: pdfColorMode,
          driveAccessToken, driveParentFolderId: driveSaveTarget?.id,
          notifyEmail,
          // issue #111: backend on_done ガード用に自動保存フラグを送る。
          autoSave: autoSaveEnabled,
        },
        async (progress) => {
          if (!isMountedRef.current) return;
          // 初回通知 (jobId 確定時) に抽出済み PDF を先行保存する。reload 後に
          // handleDone が読み出して originalPdfBlob を復元する (async reload F6)。
          // issue #126: 初回判定 (activeJobId) と entry 保存 / マーカー削除は await 前に同期実行し、
          // onProgress が同期連続 (queued→processing) で呼ばれても二重先行保存や entry 保存の後ろ倒しに
          // ならないようにする。 元 PDF 書込のみ末尾で await して書込レースを解消する。
          const isFirstProgress = activeJobId === null;
          activeJobId = progress.jobId;
          setJobProgress(progress);
          saveJobLifecycleEntry({
            jobId: progress.jobId,
            jobType: 'from_pdf',
            status: progress.status,
            createdAt: Date.now(),
            // issue #110: 再投入で元設定を忠実再現するため拡張 params も保存する。
            // issue #152: originalTitle (命名ソース) も保存する。
            params: { fileName, originalTitle, part, mode, scordatura, bpm, colorMode: pdfColorMode },
          });
          // 書込順序: entry → マーカー削除。entry があれば hydration スキップされるため
          // 「両方無い」瞬間が発生しない安全側 (spec §5-3、plan §0.2)。
          clearPendingSubmitMarker();
          if (isFirstProgress) {
            // issue #115 (cold start): 初回 jobId 受領 = サーバ稼働の最強シグナル。 health を即健全化し
            // error 固着した「サーバ起動中」banner を消す (OFF 経路の recovery)。
            markHealthy();
            // issue #126: 書込レース解消のため await 化 (未コミット中の復元で null になるのを防ぐ)。
            await savePendingOriginalPdf(progress.jobId, pdfBlob);
          }
        },
      );
      // issue #36 #71 パターンB: 「保存 → 表示」順を厳守する。
      // 元 PDF をエディタ再生成用に保持 (regeneratePdf/regenerateBoth の第1引数で使う)。
      // File は Blob 継承のため originalPdfBlob にそのまま格納可能。
      // ★この saveFingeringResult を setResultUrl より前に await することで、
      // 「結果は見えてるが IndexedDB 未コミット」窓 (reload で消失) を消す (koizumi #70 真因)。
      await saveFingeringResult({
        pdfBlob: result.pdfBlob,
        fileName,
        // issue #152: 命名ソース (元タイトル) を永続化し、 以降の再生成でも同名を再現する。
        originalTitle,
        audioBlob: result.audioBlob,
        audioFileName: result.audioFileName,
        fingeringJson: result.fingeringJson,
        // エディタ背景は分割済み PDF (issue #34) を優先。未返却時は投入 PDF (抽出済み) を背景に使う。
        originalPdfBlob: result.backgroundPdfBlob ?? (result.fingeringJson ? pdfBlob : undefined),
        mxlBlob: result.mxlBlob,
        detectedBpm: result.detectedBpm,
        // issue #81: reload しても Drive 保存ボタンを再 enable できるよう mode を永続化する。
        mode,
      });

      // 保存完了後にのみ結果カードを表示する (上の save 完了が表示の前提)。
      const url = URL.createObjectURL(result.pdfBlob);
      setResultUrl(url);
      setResultFileName(fileName);

      if (features.enableAudio && result.audioBlob) {
        const aUrl = URL.createObjectURL(result.audioBlob);
        setAudioUrl(aUrl);
        setAudioFileName(result.audioFileName || 'audio.mp3');
        setDetectedBpm(result.detectedBpm);
      }

      if (result.fingeringJson) {
        setFingeringJson(result.fingeringJson);
      }
      // Drive 保存用スナップショット更新 (issue #81: mode は saveFingeringResult で永続化済み)
      setCurrentPart(part);
      // issue #152: 表示・Drive 保存 title は元タイトル (完成 PDF 名を使わない)。
      setCurrentTitle(originalTitle);
      const refreshed = await loadFingeringResult();
      if (refreshed) setStoredSnapshot(refreshed);

      // issue #67 Phase G: backend 自動保存ステータスを反映。
      // 'ok' のとき手動 Drive 保存ボタンを抑止し「✅ 自動保存済」を表示。
      // token_expired / drive_error は notice として表示し、 手動ボタンを残す。
      // 旧 backend (auto_save_status 未返却) は undefined → 既存挙動 (手動ボタン有効)。
      if (result.autoSaveStatus !== undefined) {
        setAutoSaveStatus(result.autoSaveStatus);
        // issue #154: backend 自動保存成功 = Drive 上に存在する。 フラグを永続化して
        // リロード後も「Drive保存済み」表示 / 共有リンクコピー活性を維持する。
        if (result.autoSaveStatus === 'ok') {
          markDriveSaved(fileName);
          setDriveSaved(true);
        }
      }

      // issue #83: page-split で OMR 救済できず skip されたページがあれば warn toast で通知する。
      // 初回 result 受領経路 (この同期 processFromPdf 完了点) のみ発火し、復元/再描画では出さない。
      if (result.skippedPages && result.skippedPages.length > 0) {
        const pages = result.skippedPages.map((p) => `p.${p}`).join(', ');
        showToast(
          `⚠️ ${pages} は認識できませんでした（他ページの運指は出力済みです）`,
          { kind: 'warn', durationMs: 8000 },
        );
      }

      // ここまで到達 = done 正常完了。 finally での entry 掃除を許可する。
      completedSuccessfully = true;
    } catch (err) {
      setError(resolveSubmitError(err, '処理に失敗しました'));
    } finally {
      // entry / 先行保存 PDF の掃除は done 正常完了時のみ。 throw 時 (transient 404→expired 等)
      // は entry を残し、reload 後 resume が再挑戦できるようにする (issue #36 F-2b)。
      if (activeJobId && completedSuccessfully) {
        clearJobLifecycleEntry(activeJobId);
        void clearPendingOriginalPdf(activeJobId);
      }
      // POST 同期エラー (jobId 未受領) でも投入中マーカーを掃除する (issue #9 reopen)。
      clearPendingSubmitMarker();
      setJobProgress(null);
      setIsProcessing(false);
    }
  }, [resultUrl, audioUrl, pdfColorMode, autoSaveEnabled, registerJob, showToast, driveAuth, markHealthy]);

  const handleFileChange = useCallback(() => {
    setError(null);
    // issue #89: 自動保存 ON は投入後も前回結果カードを表示維持する方針 (handleSubmit と一致)。
    // ファイル選択時点で結果をクリアすると投入前に結果カードが消えてしまうため、 ON では
    // 結果系 state を温存する。 OFF (同期投入) は従来どおりファイル選択でクリアする。
    if (getAutoSaveEnabled()) return;
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setResultUrl(null);
    setAudioUrl(null);
    setResultFileName('');
    setAudioFileName('');
    setDetectedBpm(undefined);
    setStoredSnapshot(null);
  }, [resultUrl, audioUrl]);

  const handleDownload = useCallback(() => {
    downloadBlobUrl(resultUrl, resultFileName);
  }, [resultUrl, resultFileName]);

  // issue #32 PR2 (D-3): Download の色変更は state/localStorage を SSOT として更新し、
  // 既存の pendingAction='pdf' 再生成経路 (navigate state) を発火させて新色 PDF を作り直す。
  const handlePdfColorModeChange = useCallback((mode: PdfColorMode) => {
    if (mode === pdfColorMode) return;
    setPdfColorMode(mode);
    savePdfColorMode(mode);
    navigate(location.pathname, {
      state: { pendingAction: 'pdf', requestedAt: Date.now(), colorMode: mode },
    });
  }, [pdfColorMode, navigate, location.pathname]);

  const handleAudioDownload = useCallback(() => {
    downloadBlobUrl(audioUrl, audioFileName);
  }, [audioUrl, audioFileName]);

  // issue #152: Drive 手動保存の命名ソース (元タイトル + part)。 投入直後は currentTitle /
  // currentPart が埋まっているが、 reload / 履歴復元では空なので stored から解決する。 これで
  // 自動保存・手動保存・再生成が同じ omfz 名に収束する (同一結果の omfz が増殖しない)。
  const storedMeta = useMemo(
    () => readStoredMeta(storedSnapshot?.fingeringJson),
    [storedSnapshot],
  );
  const driveSaveTitle = resolveOriginalTitle({
    originalTitle: storedSnapshot?.originalTitle,
    metaTitle: currentTitle || storedMeta.title,
    fileName: storedSnapshot?.fileName ?? resultFileName,
  });
  const driveSavePart = currentPart ?? toFingeringPart(storedMeta.part);

  return (
    <div ref={scrollContainerRef} className="fixed inset-0 bg-gradient-to-b from-amber-50/80 via-white to-orange-50/40 overflow-y-auto font-rounded">
      <div className="flex flex-col min-h-full">
        {/* Header */}
        <div className="pt-6 pb-3 px-4 flex items-center justify-center gap-2.5 relative">
          <img
            src="/omakase-unshi-kun.png"
            alt="おまかせ運指くん"
            className="w-10 h-10 sm:w-12 sm:h-12 drop-shadow-md"
          />
          <h1 className="text-xl sm:text-2xl font-extrabold bg-gradient-to-r from-amber-600 via-orange-500 to-brand-purple bg-clip-text text-transparent tracking-tight">
            おまかせ運指くん
          </h1>
        </div>

        {/* pre-jobID 区間オーバーレイ (issue #2) */}
        <SubmittingOverlay visible={isSubmitting} />

        {/* issue #85 round 2: fire-and-forget done の完了モーダル (createPortal で body 直下)。 */}
        {completionModal && (
          <CompletionModal
            fileName={completionModal.fileName}
            onOpenResult={handleOpenResultFromModal}
            onClose={handleCloseCompletionModal}
          />
        )}

        {/* issue #116 FE-2: fire-and-forget error のエラーモーダル (完了モーダルの鏡像)。 */}
        {errorModal && (
          <ErrorModal
            jobId={errorModal.jobId}
            errorKind={errorModal.errorKind}
            errorMessage={errorModal.errorMessage}
            onRetry={handleRetryFromErrorModal}
            onClose={handleCloseErrorModal}
          />
        )}

        {/* Main Card */}
        <div className="flex-1 px-3 sm:px-4 pb-6">
          <div className="sm:max-w-md mx-auto bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl shadow-amber-900/5 border border-amber-100/60 p-5 sm:p-6">
            {/* issue #76 Phase 4-β: 画面非表示中に完了した未通知 done entry を画面復帰時に通知。 */}
            {bannerEntry && (
              <CompletionBanner
                entry={bannerEntry}
                onDismiss={handleDismissBanner}
                onOpenHistory={handleOpenHistoryFromBanner}
              />
            )}
            {/* issue #115 T3 / #132: cold start (F1 アイドルアンロード) 中は score-engine の起動を待つ案内を出す。
                issue #141: 接続エラー (ERR_CONNECTION_REFUSED 等 / reject) でも health fetch が失敗している
                間は serverStarting=true になるので banner を出す。 健全化 (ok) で消え、 初期 pending は
                serverStarting=false で非表示 (#132 フラッシュ防止)。 scoreEngine ではなく serverStarting を見る。
                #109 の処理中経過表示 (processingMessage) とは独立に常時判定。 */}
            {health?.serverStarting && (
              <div role="status" className="mb-3 px-1">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                  <span aria-hidden="true">⏳</span>
                  <span>サーバ起動中です。初回は最大60秒ほどかかります…</span>
                </div>
              </div>
            )}
            <FingeringForm
              onSubmit={handleSubmit}
              onFileChange={handleFileChange}
              isProcessing={showFormSpinner}
              processingMessage={processingMessage}
              elapsedFromMs={activeJobs[0]?.createdAt}
              hasResult={!!resultUrl}
              restoreRequest={restoreRequest}
              restoreNonce={restoreNonce}
            />

            {/* issue #67 Phase G: 自動保存 ON/OFF toggle (default ON、 localStorage 永続)
                issue #98: 完了メール通知 toggle (default OFF、 ON のときメアド欄) */}
            {/* issue #67 Phase G: 自動保存 ON/OFF toggle (default ON、 localStorage 永続)
                issue #98: 完了メール通知 toggle (default OFF、 ON のときメアド欄) */}
            <div className="mt-3 px-1 flex flex-col gap-2">
              <AutoSaveToggle
                enabled={autoSaveEnabled}
                onChange={(next) => {
                  persistAutoSaveEnabled(next);
                  setAutoSaveEnabled(next);
                }}
              />
              <NotifyToggle disabled={!autoSaveEnabled} />
            </div>

            {/* Error */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-start gap-2 animate-fade-in-up">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.27 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                {/* issue #54/#76 Phase 5-β: buildFingeringErrorMessage の改行を活かすため whitespace-pre-line */}
                <span className="whitespace-pre-line">{error}</span>
              </div>
            )}

            {/* 復元失敗 (issue #36 M-2): reload 復元が expired / 取得失敗 / failed で
                確定したときの明示メッセージ + 再投入導線。 上のフォームから再アップロードできる。 */}
            {resumeFailureMessage && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm flex items-start gap-2 animate-fade-in-up">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.27 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>{resumeFailureMessage}</span>
              </div>
            )}

            {/* Results — 再生成中は古い結果を隠す (UX 混乱防止) */}
            {resultUrl && !isProcessing && (
              <div className="mt-6 space-y-3 animate-fade-in-up">
                {/* issue #153: 生成結果ブロックの先頭にラベル付き仕切りを表示 */}
                <SectionDivider label="生成結果" />
                <div className="text-center mb-4 flex flex-col items-center gap-2">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-600 rounded-full text-xs font-bold border border-green-100">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    生成完了
                  </div>
                  {/* issue #84 ケースA: 前回結果を表示しつつ、 別ジョブが投入中なら件数を併記。 */}
                  <ActiveJobsBadge count={activeJobsCount} onClick={() => setHistoryOpen(true)} />
                </div>

                {/* PDF Download (issue #32 PR2: 色選択 Split Button 化) */}
                <PdfDownloadSplitButton
                  pdfColorMode={pdfColorMode}
                  onPdfColorModeChange={handlePdfColorModeChange}
                  onDownload={handleDownload}
                  fileName={resultFileName}
                />

                {/* Audio Download */}
                {features.enableAudio && audioUrl && (
                  <button
                    onClick={handleAudioDownload}
                    className="w-full group flex items-center gap-3 p-3 sm:p-3.5 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/60 rounded-xl hover:border-orange-300 hover:shadow-md hover:shadow-orange-100 transition-all active:scale-[0.98]"
                  >
                    <div className="w-10 h-10 rounded-lg bg-accent-orange flex items-center justify-center flex-shrink-0 shadow-sm">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-700">練習用音源</div>
                      <div className="text-xs text-gray-400 truncate">{audioFileName}</div>
                      {detectedBpm && (
                        <div className="text-xs text-amber-600 mt-0.5">楽譜から検出: {detectedBpm} BPM</div>
                      )}
                    </div>
                    <svg className="w-5 h-5 text-gray-300 group-hover:text-orange-500 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>
                )}

                {/* Editor button */}
                {hasFingeringData && (
                  <button
                    onClick={() => navigate('/fingering/editor')}
                    className="w-full group flex items-center gap-3 p-3 sm:p-3.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/60 rounded-xl hover:border-blue-300 hover:shadow-md hover:shadow-blue-100 transition-all active:scale-[0.98]"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-white text-lg">🎸</span>
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-700">エディタで開く</div>
                      <div className="text-xs text-gray-400">運指を手動で修正・追加</div>
                    </div>
                    <svg className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}

                {/* Drive 保存 (Drive 設定済み + 結果あり)。保存先変更は DriveFooterBar 側 */}
                {/* issue #154: Drive 保存済み (localStorage 永続フラグ) なら保存ボタンを保存済み表示に置換。
                    手動保存 / 自動保存 のどちらで保存されたかを問わず、 リロード後も維持される。 */}
                {isDriveConfigured && hasFingeringData && storedSnapshot && (
                  driveSaved ? (
                    <div
                      data-testid="drive-saved-indicator"
                      className="w-full flex items-center gap-3 p-3 sm:p-3.5 bg-gray-50 border border-gray-200 rounded-xl"
                    >
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="text-left flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-700">Drive保存済み ✓</div>
                        <div className="text-xs text-gray-400">下のボタンで共有 URL をコピーできます</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <DriveSaveButton
                        stored={storedSnapshot}
                        // issue #152: title は元タイトル契約。 stored.originalTitle → meta.title →
                        // 完成 PDF 名 derive の順で解決する (二重「運指済」/ unknown 混入を防ぐ)。
                        title={driveSaveTitle}
                        part={toPartLabel(driveSavePart)}
                        target={driveTarget}
                        onSaved={() => setDriveSaved(true)}
                      />
                      {/* issue #67 Phase G: 自動保存失敗の通知 (手動 fallback を促す) */}
                      {(autoSaveStatus === 'token_expired' || autoSaveStatus === 'drive_error') && (
                        <p className="mt-1.5 text-xs text-amber-700 px-2">
                          自動保存に失敗しました。 手動で保存してください。
                        </p>
                      )}
                    </>
                  )
                )}

                {/* issue #140: Drive 保存済み PDF の共有リンクをコピー (自動保存済でも有用) */}
                {/* issue #142: 未保存のあいだは非活性。 issue #154: 共有 URL は都度取得する。 */}
                {isDriveConfigured && hasFingeringData && storedSnapshot && (
                  <DriveLinkCopyButton
                    stored={storedSnapshot}
                    target={driveTarget}
                    savedToDrive={driveSaved}
                    onStale={() => {
                      // issue #154: Drive 上に無い = フラグ陳腐化。 クリアして再保存を促す。
                      clearDriveSaved(storedSnapshot.fileName);
                      setDriveSaved(false);
                    }}
                  />
                )}

              </div>
            )}

            {/* issue #84 ケースB: 結果なし・初回投入中は件数バッジを単独表示 (前回結果は隠さない)。 */}
            {!resultUrl && (
              <div className="mt-4 text-center">
                <ActiveJobsBadge count={activeJobsCount} onClick={() => setHistoryOpen(true)} />
              </div>
            )}
          </div>
        </div>

        {/* Drive Footer Bar (画面最下部、Footer 直前)。クリックで保存先変更 */}
        <div className="px-3 sm:px-4">
          <DriveFooterBar
            target={driveTarget}
            onChangeFolder={handleDriveFolderChanged}
            isHistoryOpen={historyOpen}
            onRequestOpenHistory={() => setHistoryOpen(true)}
            onCloseHistory={() => setHistoryOpen(false)}
            onRestoreJob={handleRestoreToForm}
          />
        </div>

        {/* Footer: 姉妹サービス aki-sta 導線。issue #133: feature flag で表示制御 (未設定/false は非表示)。 */}
        {process.env.REACT_APP_ENABLE_AKISTA_LINK === 'true' && (
          <div className="py-5 text-center">
            <a
              href="https://delightful-smoke-0d4827500.1.azurestaticapps.net"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-amber-700 bg-white/60 border border-amber-200 rounded-full hover:bg-white hover:border-amber-300 hover:shadow-md hover:shadow-amber-100 transition-all"
            >
              <img src="/aki-sta-search-kun.png" alt="" className="w-5 h-5" />
              姉妹サービス: 空きスタサーチくんへ
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        )}

        {/* issue #101 バグ①: ステータス pill をページ内フロー最下部へ移設 (App.tsx 共通描画から移動)。
            issue #106: 解析エンジン pill は技術ステータスなので Copyright のさらに下・右下へ薄く格下げする
            (フッターの主役は姉妹サービス導線、技術ステータスは気づく人だけ気づくサイズに)。 */}
        <Copyright />
        {/* issue #123: OAuth 一般公開の必須要件 = 同一ドメイン上の privacy policy / 利用規約 導線。
            SPA ルートでなく public/ 直下の静的 HTML (JS 不要で SWA が直接配信) を指す。 */}
        <nav className="flex items-center justify-center gap-2.5 text-xs text-gray-500 mt-1.5">
          <Link to="/help" className="hover:text-gray-700 hover:underline transition-colors">ヘルプ・ご要望</Link>
          <span aria-hidden="true" className="text-gray-300">·</span>
          <a href="/privacy.html" className="hover:text-gray-700 hover:underline transition-colors">プライバシーポリシー</a>
          <span aria-hidden="true" className="text-gray-300">·</span>
          <a href="/terms.html" className="hover:text-gray-700 hover:underline transition-colors">利用規約</a>
        </nav>
        <AppFooter />
      </div>
      <AddToHomeModal isOpen={addToHomePrompt.isOpen} onClose={addToHomePrompt.close} />
    </div>
  );
};

export default FingeringPage;
